import React, { useState, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    ArrowLeft,
    Download,
    RefreshCw,
    Building2,
    Calendar,
    MapPin,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Clock,
    FileText,
    Edit,
    Eye,
    Upload,
    Search,
    History,
    File,
    FileCheck,
    FilePlus,
    Trash2,
    BarChart3,
    Loader2,
    X,
    Leaf,
    Droplets,
    Zap,
    Users,
    TrendingDown,
    Plus
} from 'lucide-react';
import ReportPreviewModal from '../components/Rapports/ReportPreviewModal';
import { downloadLoanReport, downloadFullLoanReport } from '../utils/pdfExport';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    Legend
} from 'recharts';
import { useLoan, useCovenants, useAlerts, useDocuments, useCovenantHistory, useCovenantMutations, useDocumentMutations, useESGMetrics, useESGCovenants, useESGMutations } from '../hooks';
import { formatCurrency, getStatusColor } from '../utils/helper';
import { useTranslation } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useActivityLog, LOG_TYPES, LOG_CATEGORIES } from '../context/ActivityLogContext';


const StatusBadge = ({ status, size = 'default', t }) => {
    const normalizedStatus = status === 'compliant' ? 'ok' : status === 'at_risk' ? 'warning' : status === 'breach' ? 'critical' : status;
    const colors = getStatusColor(normalizedStatus);
    const icons = { ok: CheckCircle, warning: AlertTriangle, critical: XCircle, active: CheckCircle, archived: CheckCircle };
    const labels = {
        ok: t ? t('loanDetail.status.compliant') : 'Compliant',
        warning: t ? t('loanDetail.status.atRisk') : 'At Risk',
        critical: t ? t('loanDetail.status.breach') : 'Breach',
        active: 'Active',
        archived: 'Archived'
    };
    const Icon = icons[normalizedStatus] || CheckCircle;
    const sizeClasses = size === 'large'
        ? 'px-4 py-2 text-sm gap-2'
        : 'px-2.5 py-1 text-xs gap-1.5';

    return (
        <span className={`inline-flex items-center rounded-full font-medium border ${colors.bg} ${colors.text} ${colors.border} ${sizeClasses}`}>
      <Icon size={size === 'large' ? 18 : 14} />
            {labels[normalizedStatus] || status}
    </span>
    );
};


const CovenantGauge = ({ covenant }) => {
    const { name, current, threshold, operator, status } = covenant;


    let percentage;
    if (operator === '<=') {
        percentage = Math.min((current / threshold) * 100, 120);
    } else {
        percentage = Math.min((threshold / current) * 100, 120);
    }

    const isBreached = status === 'critical';
    const isWarning = status === 'warning';

    const barColor = isBreached
        ? 'bg-red-500'
        : isWarning
            ? 'bg-amber-500'
            : 'bg-emerald-500';

    const bgColor = isBreached
        ? 'bg-red-100'
        : isWarning
            ? 'bg-amber-100'
            : 'bg-slate-100';

    return (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h4 className="font-semibold text-slate-800">{name}</h4>
                    <p className="text-sm text-slate-500 mt-0.5">
                        Threshold: {operator} {threshold.toLocaleString()}
                    </p>
                </div>
                <StatusBadge status={status} />
            </div>

            <div className="flex items-end justify-between mb-2">
        <span className="text-3xl font-bold text-slate-800">
          {current.toLocaleString()}
        </span>
                <span className={`text-sm font-medium ${
                    isBreached ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-emerald-600'
                }`}>
          {operator === '<='
              ? `${((threshold - current) / threshold * 100).toFixed(1)}% headroom`
              : `${((current - threshold) / threshold * 100).toFixed(1)}% above min`
          }
        </span>
            </div>

            {}
            <div className={`h-3 rounded-full overflow-hidden ${bgColor}`}>
                <div
                    className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                />
            </div>

            {}
            <div className="relative mt-1">
                <div
                    className="absolute top-0 w-0.5 h-2 bg-slate-400"
                    style={{ left: '100%', transform: 'translateX(-50%)' }}
                />
                <div className="flex justify-between text-xs text-slate-400 mt-2">
                    <span>0</span>
                    <span>Threshold: {threshold.toLocaleString()}</span>
                </div>
            </div>
        </div>
    );
};


const generateHistoricalData = (covenants) => {
    const months = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map((month, index) => {
        const data = { month };
        covenants.forEach(cov => {

            const variation = (Math.random() - 0.5) * 0.3;
            data[cov.name] = +(cov.current * (1 + variation - index * 0.02)).toFixed(2);
            data[`${cov.name}_threshold`] = cov.threshold;
        });
        return data;
    });
};


const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${bytes} B`;
};

function LoanDetail() {
    const { id } = useParams();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { addLog } = useActivityLog();
    const [activeTab, setActiveTab] = useState('overview');
    const [showReportModal, setShowReportModal] = useState(false);
    const [exportSuccess, setExportSuccess] = useState(false);


    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [updateMessage, setUpdateMessage] = useState({ type: '', text: '' });


    const [covenantUpdates, setCovenantUpdates] = useState({});
    const [updateNotes, setUpdateNotes] = useState('');


    const [uploadFile, setUploadFile] = useState(null);
    const [uploadForm, setUploadForm] = useState({
        name: '',
        type: 'facility_agreement'
    });


    const [covenantFilter, setCovenantFilter] = useState('all');
    const [historyFilter, setHistoryFilter] = useState('all');
    const [documentFilter, setDocumentFilter] = useState('all');
    const [documentSearch, setDocumentSearch] = useState('');
    const [showESGModal, setShowESGModal] = useState(false);
    const [esgForm, setEsgForm] = useState({
        period: new Date().toISOString().slice(0, 7),
        esg_score: '',
        environmental_score: '',
        social_score: '',
        governance_score: '',
        carbon_intensity: '',
        renewable_energy_percent: '',
        water_consumption: '',
        gender_diversity_percent: ''
    });
    const [isSubmittingESG, setIsSubmittingESG] = useState(false);


    const { loan: dbLoan, loading: loanLoading, refetch: refetchLoan } = useLoan(id);
    const { covenants: dbCovenants, loading: covenantsLoading, refetch: refetchCovenants } = useCovenants(id);
    const { alerts: dbAlerts, loading: alertsLoading } = useAlerts({ loanId: parseInt(id), isArchived: false });
    const { documents: dbDocuments, loading: documentsLoading, refetch: refetchDocuments } = useDocuments(id);
    const { history: dbHistory, loading: historyLoading } = useCovenantHistory(id);
    const { metrics: esgMetrics, loading: esgLoading, refetch: refetchESG } = useESGMetrics(id);
    const { esgCovenants, loading: esgCovenantsLoading } = useESGCovenants(id);


    const { updateCovenantValue } = useCovenantMutations();
    const { uploadDocument, deleteDocument } = useDocumentMutations();
    const { addMetric } = useESGMutations();


    const loan = useMemo(() => {
        if (!dbLoan) return null;
        return {
            id: dbLoan.id,
            borrower: dbLoan.borrower,
            leadArranger: dbLoan.lead_arranger,
            bank: dbLoan.bank,
            amount: dbLoan.amount,
            currency: dbLoan.currency,
            startDate: dbLoan.start_date,
            maturityDate: dbLoan.maturity_date,
            nextReportingDate: dbLoan.next_reporting_date,
            sector: dbLoan.sector,
            country: dbLoan.country,
            status: dbLoan.status,
            covenants: dbCovenants.map(c => ({
                id: c.id,
                name: c.name,
                threshold: c.threshold,
                operator: c.operator,
                current: c.current_value,
                status: c.status,
                type: c.type,
                frequency: c.frequency
            }))
        };
    }, [dbLoan, dbCovenants]);


    const loanAlerts = useMemo(() => {
        return dbAlerts.map(alert => ({
            id: alert.id,
            loanId: alert.loan_id,
            type: alert.type,
            category: alert.category,
            title: alert.title,
            message: alert.message,
            isRead: Boolean(alert.is_read),
            createdAt: alert.created_at
        }));
    }, [dbAlerts]);


    const historicalData = useMemo(() => {
        if (!loan) return [];
        return generateHistoricalData(loan.covenants);
    }, [loan]);


    const documents = useMemo(() => {
        return dbDocuments.map(doc => ({
            id: doc.id,
            name: doc.name,
            type: doc.type,
            size: formatFileSize(doc.file_size),
            uploadedAt: doc.uploaded_at,
            uploadedBy: doc.uploaded_by_name || 'Unknown'
        }));
    }, [dbDocuments]);


    const breachHistory = useMemo(() => {
        return dbHistory
            .filter(h => h.status === 'critical' || h.status === 'warning')
            .map(h => ({
                id: h.id,
                date: h.recorded_at,
                covenant: h.covenant_name,
                value: h.value,
                threshold: h.threshold,
                status: 'resolved'
            }));
    }, [dbHistory]);


    const historyEvents = useMemo(() => {
        return dbHistory.map(h => ({
            id: h.id,
            date: h.recorded_at,
            type: h.status === 'critical' ? 'breach' : h.status === 'warning' ? 'breach' : 'update',
            title: h.status === 'critical' ? `${h.covenant_name} breach detected` :
                   h.status === 'warning' ? `${h.covenant_name} warning` :
                   `${h.covenant_name} updated`,
            description: `Value: ${h.value} (Threshold: ${h.threshold})`,
            user: h.recorded_by_name || 'System',
            status: h.status === 'ok' ? undefined : 'resolved'
        }));
    }, [dbHistory]);


    const chartColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];


    const handleQuickExport = () => {
        if (loan) {
            downloadLoanReport(loan, loanAlerts, breachHistory);
            setExportSuccess(true);
            setTimeout(() => setExportSuccess(false), 2000);
        }
    };


    const handleFullReportExport = () => {
        if (loan) {
            downloadFullLoanReport(loan, loanAlerts, breachHistory, historicalData);
            setExportSuccess(true);
            setTimeout(() => setExportSuccess(false), 2000);
        }
    };


    const handleOpenUpdateModal = useCallback(() => {
        const initialUpdates = {};
        loan?.covenants?.forEach(cov => {
            initialUpdates[cov.id] = cov.current;
        });
        setCovenantUpdates(initialUpdates);
        setUpdateNotes('');
        setShowUpdateModal(true);
    }, [loan]);


    const handleUpdateCovenants = async () => {
        setIsUpdating(true);
        setUpdateMessage({ type: '', text: '' });

        try {
            const updates = Object.entries(covenantUpdates).filter(([covenantId, value]) => {
                const covenant = loan.covenants.find(c => String(c.id) === String(covenantId));
                const currentValue = covenant?.current_value ?? covenant?.current;
                return covenant && currentValue !== parseFloat(value);
            });

            for (const [covenantId, value] of updates) {
                const result = await updateCovenantValue(covenantId, parseFloat(value), user?.id, updateNotes);
                if (!result.success) {
                    throw new Error(result.error || t('loanDetail.updateError'));
                }
            }

            setUpdateMessage({ type: 'success', text: t('loanDetail.updateSuccess') });


            addLog({
                type: LOG_TYPES.ACTION,
                category: LOG_CATEGORIES.COVENANT,
                action: 'update_covenants',
                message: `Covenants mis à jour pour ${loan.borrower}`,
                details: {
                    loanId: loan.id,
                    borrower: loan.borrower,
                    updatedCount: updates.length,
                    notes: updateNotes
                }
            });

            await refetchCovenants();
            await refetchLoan();
            setTimeout(() => {
                setShowUpdateModal(false);
                setUpdateMessage({ type: '', text: '' });
            }, 1500);
        } catch (error) {
            setUpdateMessage({ type: 'error', text: error.message || t('loanDetail.updateError') });
        }

        setIsUpdating(false);
    };


    const handleUploadDocument = async () => {
        if (!uploadFile) return;

        setIsUploading(true);
        setUpdateMessage({ type: '', text: '' });

        try {

            const reader = new FileReader();
            reader.onload = async () => {
                const base64Data = reader.result.split(',')[1];

                const documentData = {
                    loanId: parseInt(id),
                    name: uploadForm.name || uploadFile.name,
                    type: uploadForm.type,
                    fileData: base64Data,
                    fileName: uploadFile.name,
                    fileSize: uploadFile.size,
                    mimeType: uploadFile.type
                };

                const result = await uploadDocument(documentData, user?.id);

                if (result.success) {
                    setUpdateMessage({ type: 'success', text: t('loanDetail.uploadSuccess') });


                    addLog({
                        type: LOG_TYPES.ACTION,
                        category: LOG_CATEGORIES.DOCUMENT,
                        action: 'upload_document',
                        message: `Document téléversé: ${uploadForm.name || uploadFile.name}`,
                        details: {
                            loanId: loan.id,
                            borrower: loan.borrower,
                            documentName: uploadForm.name || uploadFile.name,
                            documentType: uploadForm.type
                        }
                    });

                    await refetchDocuments();
                    setTimeout(() => {
                        setShowUploadModal(false);
                        setUploadFile(null);
                        setUploadForm({ name: '', type: 'facility_agreement' });
                        setUpdateMessage({ type: '', text: '' });
                    }, 1500);
                } else {
                    throw new Error(result.error || t('loanDetail.uploadError'));
                }

                setIsUploading(false);
            };

            reader.onerror = () => {
                setUpdateMessage({ type: 'error', text: t('loanDetail.uploadError') });
                setIsUploading(false);
            };

            reader.readAsDataURL(uploadFile);
        } catch (error) {
            setUpdateMessage({ type: 'error', text: error.message || t('loanDetail.uploadError') });
            setIsUploading(false);
        }
    };


    const handleDeleteDocument = async (docId) => {
        if (!window.confirm(t('common.confirmDelete'))) return;

        const doc = documents.find(d => d.id === docId);

        try {
            const result = await deleteDocument(docId);
            if (result.success) {

                addLog({
                    type: LOG_TYPES.ACTION,
                    category: LOG_CATEGORIES.DOCUMENT,
                    action: 'delete_document',
                    message: `Document supprimé: ${doc?.name || 'Unknown'}`,
                    details: {
                        loanId: loan.id,
                        borrower: loan.borrower,
                        documentName: doc?.name,
                        documentId: docId
                    }
                });
                await refetchDocuments();
            }
        } catch (error) {
            console.error('Error deleting document:', error);
        }
    };


    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setUploadFile(file);
            if (!uploadForm.name) {
                setUploadForm(prev => ({ ...prev, name: file.name.replace(/\.[^/.]+$/, '') }));
            }
        }
    };


    const handleSubmitESG = async () => {
        setIsSubmittingESG(true);
        setUpdateMessage({ type: '', text: '' });

        try {
            const metricData = {
                loan_id: id,
                period: esgForm.period,
                esg_score: esgForm.esg_score ? parseFloat(esgForm.esg_score) : null,
                environmental_score: esgForm.environmental_score ? parseFloat(esgForm.environmental_score) : null,
                social_score: esgForm.social_score ? parseFloat(esgForm.social_score) : null,
                governance_score: esgForm.governance_score ? parseFloat(esgForm.governance_score) : null,
                carbon_intensity: esgForm.carbon_intensity ? parseFloat(esgForm.carbon_intensity) : null,
                renewable_energy_percent: esgForm.renewable_energy_percent ? parseFloat(esgForm.renewable_energy_percent) : null,
                water_consumption: esgForm.water_consumption ? parseFloat(esgForm.water_consumption) : null,
                gender_diversity_percent: esgForm.gender_diversity_percent ? parseFloat(esgForm.gender_diversity_percent) : null,
                recorded_by: user?.id
            };

            const result = await addMetric(metricData);

            if (result.success) {
                setUpdateMessage({ type: 'success', text: t('loanDetail.esg.submitSuccess') });

                addLog({
                    type: LOG_TYPES.ACTION,
                    category: LOG_CATEGORIES.ESG || 'ESG',
                    action: 'submit_esg_metrics',
                    message: `ESG metrics submitted for ${loan.borrower} - Period ${esgForm.period}`,
                    details: {
                        loanId: loan.id,
                        borrower: loan.borrower,
                        period: esgForm.period,
                        esgScore: esgForm.esg_score
                    }
                });

                await refetchESG();
                setTimeout(() => {
                    setShowESGModal(false);
                    setEsgForm({
                        period: new Date().toISOString().slice(0, 7),
                        esg_score: '',
                        environmental_score: '',
                        social_score: '',
                        governance_score: '',
                        carbon_intensity: '',
                        renewable_energy_percent: '',
                        water_consumption: '',
                        gender_diversity_percent: ''
                    });
                    setUpdateMessage({ type: '', text: '' });
                }, 1500);
            } else {
                throw new Error(result.error || t('loanDetail.esg.submitError'));
            }
        } catch (error) {
            setUpdateMessage({ type: 'error', text: error.message || t('loanDetail.esg.submitError') });
        }

        setIsSubmittingESG(false);
    };


    if (loanLoading || covenantsLoading || !loan) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-4 sm:space-y-6">
            {}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                    <Link
                        to="/loans"
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors shrink-0"
                    >
                        <ArrowLeft size={20} className="text-slate-600" />
                    </Link>
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            <h1 className="text-xl sm:text-2xl font-bold text-slate-800 truncate">{loan.borrower}</h1>
                            <StatusBadge status={loan.status} size="large" t={t} />
                        </div>
                        <p className="text-sm sm:text-base text-slate-500 mt-1 truncate">
                            {formatCurrency(loan.amount)} • {loan.sector} • {loan.country}
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 sm:gap-3 ml-11 lg:ml-0">
                    <button
                        onClick={() => setShowReportModal(true)}
                        className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-700 text-sm sm:text-base"
                    >
                        <Eye size={18} />
                        <span className="hidden sm:inline">{t('loanDetail.previewReport')}</span>
                        <span className="sm:hidden">{t('loanDetail.preview')}</span>
                    </button>
                    <button
                        onClick={handleOpenUpdateModal}
                        className="hidden md:flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-700"
                    >
                        <RefreshCw size={18} />
                        {t('loanDetail.updateData')}
                    </button>
                    <button
                        onClick={handleQuickExport}
                        className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-white transition-all text-sm sm:text-base ${
                            exportSuccess ? 'bg-emerald-600' : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                    >
                        {exportSuccess ? (
                            <>
                                <CheckCircle size={18} />
                                <span className="hidden sm:inline">{t('loanDetail.downloaded')}</span>
                            </>
                        ) : (
                            <>
                                <Download size={18} />
                                <span className="hidden sm:inline">{t('loanDetail.exportPdf')}</span>
                                <span className="sm:hidden">{t('loanDetail.export')}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {}
            <div className="border-b border-slate-200 overflow-x-auto">
                <nav className="flex gap-4 sm:gap-8 min-w-max">
                    {['overview', 'covenants', 'esg', 'history', 'documents'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`pb-3 sm:pb-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
                                activeTab === tab
                                    ? 'border-blue-600 text-blue-600'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            {tab === 'esg' && <Leaf size={16} />}
                            {t(`loanDetail.tabs.${tab}`)}
                        </button>
                    ))}
                </nav>
            </div>

            {}
            {activeTab === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                    {}
                    <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                        {}
                        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
                            <h3 className="font-semibold text-slate-800 mb-4">{t('loanDetail.loanInformation')}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                        <Building2 size={20} className="text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">{t('loanDetail.leadArranger')}</p>
                                        <p className="font-medium text-slate-800">{loan.leadArranger}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-emerald-100 rounded-lg">
                                        <Calendar size={20} className="text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">{t('loanDetail.maturityDate')}</p>
                                        <p className="font-medium text-slate-800">
                                            {new Date(loan.maturityDate).toLocaleDateString('en-GB', {
                                                day: 'numeric', month: 'long', year: 'numeric'
                                            })}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-purple-100 rounded-lg">
                                        <MapPin size={20} className="text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">{t('loanDetail.countrySector')}</p>
                                        <p className="font-medium text-slate-800">{loan.country} • {loan.sector}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-amber-100 rounded-lg">
                                        <Clock size={20} className="text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">{t('loanDetail.nextReporting')}</p>
                                        <p className="font-medium text-slate-800">
                                            {new Date(loan.nextReportingDate).toLocaleDateString('en-GB', {
                                                day: 'numeric', month: 'long', year: 'numeric'
                                            })}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {}
                        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 mb-4 sm:mb-6">
                                <h3 className="font-semibold text-slate-800">Covenant Trends</h3>
                                <select className="w-full sm:w-auto px-3 py-1.5 border border-slate-300 rounded-lg text-sm">
                                    <option>Last 6 months</option>
                                    <option>Last 12 months</option>
                                    <option>Last 24 months</option>
                                </select>
                            </div>
                            <div className="h-56 sm:h-72">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={historicalData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                                        <YAxis stroke="#64748b" fontSize={12} />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#fff',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                fontSize: '12px'
                                            }}
                                        />
                                        <Legend />
                                        {loan.covenants.slice(0, 3).map((cov, index) => (
                                            <React.Fragment key={cov.name}>
                                                <Line
                                                    type="monotone"
                                                    dataKey={cov.name}
                                                    stroke={chartColors[index]}
                                                    strokeWidth={2}
                                                    dot={{ fill: chartColors[index], strokeWidth: 2, r: 4 }}
                                                    activeDot={{ r: 6 }}
                                                />
                                                <ReferenceLine
                                                    y={cov.threshold}
                                                    stroke={chartColors[index]}
                                                    strokeDasharray="5 5"
                                                    strokeOpacity={0.5}
                                                />
                                            </React.Fragment>
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        {}
                        <div>
                            <h3 className="font-semibold text-slate-800 mb-4">Covenant Status</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                {loan.covenants.slice(0, 4).map(covenant => (
                                    <CovenantGauge key={covenant.id} covenant={covenant} />
                                ))}
                            </div>
                            {loan.covenants.length > 4 && (
                                <button
                                    onClick={() => setActiveTab('covenants')}
                                    className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
                                >
                                    View all {loan.covenants.length} covenants →
                                </button>
                            )}
                        </div>
                    </div>

                    {}
                    <div className="space-y-4 sm:space-y-6">
                        {}
                        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
                            <h3 className="font-semibold text-slate-800 mb-4">Quick Stats</h3>
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-500">Total Covenants</span>
                                    <span className="font-semibold text-slate-800">{loan.covenants.length}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-500">Compliant</span>
                                    <span className="font-semibold text-emerald-600">
                                        {loan.covenants.filter(c => c.status === 'ok').length}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-500">At Risk</span>
                                    <span className="font-semibold text-amber-600">
                                        {loan.covenants.filter(c => c.status === 'warning').length}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-500">Breached</span>
                                    <span className="font-semibold text-red-600">
                                        {loan.covenants.filter(c => c.status === 'critical').length}
                                    </span>
                                </div>
                                <hr className="border-slate-200" />
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-500">Days to Maturity</span>
                                    <span className="font-semibold text-slate-800">
                                        {Math.ceil((new Date(loan.maturityDate) - new Date()) / (1000 * 60 * 60 * 24))}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {}
                        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
                            <h3 className="font-semibold text-slate-800 mb-4">Recent Alerts</h3>
                            {loanAlerts.length > 0 ? (
                                <div className="space-y-3">
                                    {loanAlerts.slice(0, 3).map(alert => (
                                        <div key={alert.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                                            {alert.type === 'critical' && <XCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />}
                                            {alert.type === 'warning' && <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />}
                                            <div>
                                                <p className="text-sm text-slate-700">{alert.title}</p>
                                                <p className="text-xs text-slate-500 mt-1">
                                                    {new Date(alert.createdAt).toLocaleDateString()}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-slate-500 text-center py-4">No recent alerts</p>
                            )}
                        </div>

                        {}
                        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
                            <h3 className="font-semibold text-slate-800 mb-4">Breach History</h3>
                            <div className="space-y-3">
                                {breachHistory.slice(0, 3).map(breach => (
                                    <div key={breach.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                        <div>
                                            <p className="text-sm font-medium text-slate-800">{breach.covenant}</p>
                                            <p className="text-xs text-slate-500">
                                                {new Date(breach.date).toLocaleDateString()} • {breach.value} vs {breach.threshold}
                                            </p>
                                        </div>
                                        <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                                            Resolved
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <button
                                onClick={() => setActiveTab('history')}
                                className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
                            >
                                View full history →
                            </button>
                        </div>

                        {}
                        <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
                            <h3 className="font-semibold text-slate-800 mb-4">Export Options</h3>
                            <div className="space-y-3">
                                <button
                                    onClick={() => setShowReportModal(true)}
                                    className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 text-left transition-colors"
                                >
                                    <Eye size={20} className="text-blue-600" />
                                    <div>
                                        <p className="font-medium text-slate-800">Preview Report</p>
                                        <p className="text-xs text-slate-500">View before downloading</p>
                                    </div>
                                </button>
                                <button
                                    onClick={handleQuickExport}
                                    className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 text-left transition-colors"
                                >
                                    <Download size={20} className="text-emerald-600" />
                                    <div>
                                        <p className="font-medium text-slate-800">Quick Export</p>
                                        <p className="text-xs text-slate-500">Download PDF directly</p>
                                    </div>
                                </button>
                                <button
                                    onClick={handleFullReportExport}
                                    className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 text-left transition-colors"
                                >
                                    <FileText size={20} className="text-purple-600" />
                                    <div>
                                        <p className="font-medium text-slate-800">Full Report</p>
                                        <p className="text-xs text-slate-500">With historical data</p>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {}
            {activeTab === 'covenants' && (
                <div className="space-y-6">
                    {}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800">All Covenants</h2>
                            <p className="text-sm text-slate-500 mt-1">
                                {loan.covenants.length} covenants • {loan.covenants.filter(c => c.status === 'ok').length} compliant • {loan.covenants.filter(c => c.status === 'warning').length} at risk • {loan.covenants.filter(c => c.status === 'critical').length} breached
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <select
                                value={covenantFilter}
                                onChange={(e) => setCovenantFilter(e.target.value)}
                                className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            >
                                <option value="all">All Status</option>
                                <option value="ok">Compliant</option>
                                <option value="warning">At Risk</option>
                                <option value="critical">Breached</option>
                            </select>
                        </div>
                    </div>

                    {}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {loan.covenants
                            .filter(c => covenantFilter === 'all' || c.status === covenantFilter)
                            .map(covenant => (
                                <CovenantGauge key={covenant.id} covenant={covenant} />
                            ))}
                    </div>

                    {}
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                            <h3 className="font-semibold text-slate-800">Covenant Trends Over Time</h3>
                            <select className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm">
                                <option>Last 6 months</option>
                                <option>Last 12 months</option>
                                <option>Last 24 months</option>
                            </select>
                        </div>
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={historicalData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                                    <YAxis stroke="#64748b" fontSize={12} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: '#fff',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            fontSize: '12px'
                                        }}
                                    />
                                    <Legend />
                                    {loan.covenants.map((cov, index) => (
                                        <React.Fragment key={cov.name}>
                                            <Line
                                                type="monotone"
                                                dataKey={cov.name}
                                                stroke={chartColors[index % chartColors.length]}
                                                strokeWidth={2}
                                                dot={{ fill: chartColors[index % chartColors.length], strokeWidth: 2, r: 4 }}
                                                activeDot={{ r: 6 }}
                                            />
                                            <ReferenceLine
                                                y={cov.threshold}
                                                stroke={chartColors[index % chartColors.length]}
                                                strokeDasharray="5 5"
                                                strokeOpacity={0.5}
                                            />
                                        </React.Fragment>
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {}
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <div className="p-4 sm:p-6 border-b border-slate-200">
                            <h3 className="font-semibold text-slate-800">Covenant Details</h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Covenant</th>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Current Value</th>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Threshold</th>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Headroom</th>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                    {loan.covenants.map(covenant => {
                                        const headroom = covenant.operator === '<='
                                            ? ((covenant.threshold - covenant.current) / covenant.threshold * 100).toFixed(1)
                                            : ((covenant.current - covenant.threshold) / covenant.threshold * 100).toFixed(1);
                                        return (
                                            <tr key={covenant.id} className="hover:bg-slate-50">
                                                <td className="px-6 py-4">
                                                    <p className="font-medium text-slate-800">{covenant.name}</p>
                                                </td>
                                                <td className="px-6 py-4 text-slate-600">
                                                    {covenant.current.toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 text-slate-600">
                                                    {covenant.operator} {covenant.threshold.toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`font-medium ${
                                                        covenant.status === 'critical' ? 'text-red-600' :
                                                        covenant.status === 'warning' ? 'text-amber-600' : 'text-emerald-600'
                                                    }`}>
                                                        {headroom}%
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <StatusBadge status={covenant.status} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {}
            {activeTab === 'history' && (
                <div className="space-y-6">
                    {}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800">Activity History</h2>
                            <p className="text-sm text-slate-500 mt-1">
                                Complete timeline of events for this loan
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <select
                                value={historyFilter}
                                onChange={(e) => setHistoryFilter(e.target.value)}
                                className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            >
                                <option value="all">All Events</option>
                                <option value="breach">Breaches</option>
                                <option value="update">Updates</option>
                                <option value="document">Documents</option>
                                <option value="reminder">Reminders</option>
                                <option value="amendment">Amendments</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-xl border border-slate-200 p-6">
                                <h3 className="font-semibold text-slate-800 mb-6">Timeline</h3>
                                <div className="space-y-6">
                                    {historyEvents
                                        .filter(e => historyFilter === 'all' || e.type === historyFilter)
                                        .map((event, index, arr) => {
                                        const getEventIcon = () => {
                                            switch (event.type) {
                                                case 'breach': return <XCircle size={20} className="text-red-500" />;
                                                case 'update': return <RefreshCw size={20} className="text-blue-500" />;
                                                case 'document': return <FileText size={20} className="text-purple-500" />;
                                                case 'reminder': return <Clock size={20} className="text-amber-500" />;
                                                case 'amendment': return <Edit size={20} className="text-emerald-500" />;
                                                default: return <History size={20} className="text-slate-500" />;
                                            }
                                        };
                                        const getEventBg = () => {
                                            switch (event.type) {
                                                case 'breach': return 'bg-red-100';
                                                case 'update': return 'bg-blue-100';
                                                case 'document': return 'bg-purple-100';
                                                case 'reminder': return 'bg-amber-100';
                                                case 'amendment': return 'bg-emerald-100';
                                                default: return 'bg-slate-100';
                                            }
                                        };
                                        return (
                                            <div key={event.id} className="flex gap-4">
                                                <div className="flex flex-col items-center">
                                                    <div className={`p-2 rounded-full ${getEventBg()}`}>
                                                        {getEventIcon()}
                                                    </div>
                                                    {index < arr.length - 1 && (
                                                        <div className="w-0.5 h-full bg-slate-200 mt-2" />
                                                    )}
                                                </div>
                                                <div className="flex-1 pb-6">
                                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                                        <h4 className="font-medium text-slate-800">{event.title}</h4>
                                                        <div className="flex items-center gap-2">
                                                            {event.status === 'resolved' && (
                                                                <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                                                                    Resolved
                                                                </span>
                                                            )}
                                                            <span className="text-sm text-slate-500">
                                                                {new Date(event.date).toLocaleDateString('en-GB', {
                                                                    day: 'numeric', month: 'short', year: 'numeric'
                                                                })}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <p className="text-sm text-slate-600 mt-1">{event.description}</p>
                                                    <p className="text-xs text-slate-400 mt-2">by {event.user}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {}
                        <div className="space-y-6">
                            {}
                            <div className="bg-white rounded-xl border border-slate-200 p-6">
                                <h3 className="font-semibold text-slate-800 mb-4">Breach Summary</h3>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-slate-500">Total Breaches</span>
                                        <span className="font-semibold text-slate-800">{breachHistory.length}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-slate-500">Resolved</span>
                                        <span className="font-semibold text-emerald-600">
                                            {breachHistory.filter(b => b.status === 'resolved').length}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-slate-500">Active</span>
                                        <span className="font-semibold text-red-600">
                                            {breachHistory.filter(b => b.status !== 'resolved').length}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {}
                            <div className="bg-white rounded-xl border border-slate-200 p-6">
                                <h3 className="font-semibold text-slate-800 mb-4">Breach Details</h3>
                                <div className="space-y-3">
                                    {breachHistory.map(breach => (
                                        <div key={breach.id} className="p-3 bg-slate-50 rounded-lg">
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="font-medium text-slate-800">{breach.covenant}</p>
                                                <span className={`text-xs px-2 py-1 rounded-full ${
                                                    breach.status === 'resolved'
                                                        ? 'bg-emerald-100 text-emerald-700'
                                                        : 'bg-red-100 text-red-700'
                                                }`}>
                                                    {breach.status === 'resolved' ? 'Resolved' : 'Active'}
                                                </span>
                                            </div>
                                            <p className="text-sm text-slate-600">
                                                Value: <span className="text-red-600 font-medium">{breach.value}</span> vs Threshold: {breach.threshold}
                                            </p>
                                            <p className="text-xs text-slate-400 mt-1">
                                                {new Date(breach.date).toLocaleDateString('en-GB', {
                                                    day: 'numeric', month: 'long', year: 'numeric'
                                                })}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {}
            {activeTab === 'esg' && (
                <div className="space-y-6">
                    {}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                                <Leaf className="text-emerald-600" size={24} />
                                {t('loanDetail.esg.title')}
                            </h2>
                            <p className="text-sm text-slate-500 mt-1">
                                {t('loanDetail.esg.subtitle')}
                            </p>
                        </div>
                        <button
                            onClick={() => setShowESGModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                        >
                            <Plus size={18} />
                            {t('loanDetail.esg.submitMetrics')}
                        </button>
                    </div>

                    {}
                    {esgCovenants.length > 0 && (
                        <div className="bg-white rounded-xl border border-slate-200 p-6">
                            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                <TrendingDown size={20} className="text-emerald-600" />
                                {t('loanDetail.esg.covenants')}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {esgCovenants.map(covenant => (
                                    <div key={covenant.id} className="p-4 border border-slate-200 rounded-lg">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-slate-700">{covenant.metric_type}</span>
                                            <span className={`text-xs px-2 py-1 rounded-full ${
                                                covenant.status === 'met' ? 'bg-emerald-100 text-emerald-700' :
                                                covenant.status === 'missed' ? 'bg-red-100 text-red-700' :
                                                'bg-amber-100 text-amber-700'
                                            }`}>
                                                {covenant.status || 'Pending'}
                                            </span>
                                        </div>
                                        <p className="text-2xl font-bold text-slate-800">{covenant.target_value}</p>
                                        <p className="text-xs text-slate-500 mt-1">
                                            {t('loanDetail.esg.marginAdjustment')}: {covenant.margin_adjustment_bps > 0 ? '+' : ''}{covenant.margin_adjustment_bps} bps
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                                <div className="p-4 sm:p-6 border-b border-slate-200">
                                    <h3 className="font-semibold text-slate-800">{t('loanDetail.esg.metricsHistory')}</h3>
                                </div>
                                {esgLoading ? (
                                    <div className="flex items-center justify-center p-8">
                                        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                                    </div>
                                ) : esgMetrics.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-slate-50">
                                                <tr>
                                                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">{t('loanDetail.esg.period')}</th>
                                                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">{t('loanDetail.esg.esgScore')}</th>
                                                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">E</th>
                                                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">S</th>
                                                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">G</th>
                                                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase">{t('loanDetail.esg.status')}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-200">
                                                {esgMetrics.map(metric => (
                                                    <tr key={metric.id} className="hover:bg-slate-50">
                                                        <td className="px-6 py-4 font-medium text-slate-800">{metric.period}</td>
                                                        <td className="px-6 py-4">
                                                            <span className="text-lg font-bold text-emerald-600">{metric.esg_score || '-'}</span>
                                                        </td>
                                                        <td className="px-6 py-4 text-slate-600">{metric.environmental_score || '-'}</td>
                                                        <td className="px-6 py-4 text-slate-600">{metric.social_score || '-'}</td>
                                                        <td className="px-6 py-4 text-slate-600">{metric.governance_score || '-'}</td>
                                                        <td className="px-6 py-4">
                                                            <span className={`text-xs px-2 py-1 rounded-full ${
                                                                metric.verified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                                                            }`}>
                                                                {metric.verified ? t('loanDetail.esg.verified') : t('loanDetail.esg.pending')}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="p-8 text-center text-slate-500">
                                        <Leaf size={48} className="mx-auto mb-4 text-slate-300" />
                                        <p>{t('loanDetail.esg.noMetrics')}</p>
                                        <p className="text-sm mt-2">{t('loanDetail.esg.clickToSubmit')}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {}
                        <div className="space-y-6">
                            {}
                            {esgMetrics.length > 0 && (
                                <div className="bg-white rounded-xl border border-slate-200 p-6">
                                    <h3 className="font-semibold text-slate-800 mb-4">{t('loanDetail.esg.latestMetrics')}</h3>
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-emerald-100 rounded-lg">
                                                <TrendingDown size={20} className="text-emerald-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm text-slate-500">{t('loanDetail.esg.carbonIntensity')}</p>
                                                <p className="font-semibold text-slate-800">
                                                    {esgMetrics[0]?.carbon_intensity || '-'} tCO2e/M
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-blue-100 rounded-lg">
                                                <Zap size={20} className="text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm text-slate-500">{t('loanDetail.esg.renewableEnergy')}</p>
                                                <p className="font-semibold text-slate-800">
                                                    {esgMetrics[0]?.renewable_energy_percent || '-'}%
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-cyan-100 rounded-lg">
                                                <Droplets size={20} className="text-cyan-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm text-slate-500">{t('loanDetail.esg.waterConsumption')}</p>
                                                <p className="font-semibold text-slate-800">
                                                    {esgMetrics[0]?.water_consumption || '-'} m³/M
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-purple-100 rounded-lg">
                                                <Users size={20} className="text-purple-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm text-slate-500">{t('loanDetail.esg.genderDiversity')}</p>
                                                <p className="font-semibold text-slate-800">
                                                    {esgMetrics[0]?.gender_diversity_percent || '-'}%
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {}
                            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200 p-6">
                                <h3 className="font-semibold text-emerald-800 mb-3">{t('loanDetail.esg.lmaCompliance')}</h3>
                                <p className="text-sm text-emerald-700 mb-4">
                                    {t('loanDetail.esg.lmaDescription')}
                                </p>
                                <ul className="text-sm text-emerald-600 space-y-2">
                                    <li className="flex items-center gap-2">
                                        <CheckCircle size={16} />
                                        {t('loanDetail.esg.glp')}
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <CheckCircle size={16} />
                                        {t('loanDetail.esg.sllp')}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {}
            {activeTab === 'documents' && (
                <div className="space-y-6">
                    {}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800">Documents</h2>
                            <p className="text-sm text-slate-500 mt-1">
                                {documents.length} documents associated with this loan
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <div className="relative">
                                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search documents..."
                                    value={documentSearch}
                                    onChange={(e) => setDocumentSearch(e.target.value)}
                                    className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm w-64"
                                />
                            </div>
                            <select
                                value={documentFilter}
                                onChange={(e) => setDocumentFilter(e.target.value)}
                                className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                            >
                                <option value="all">All Types</option>
                                <option value="agreement">Agreements</option>
                                <option value="financial">Financial</option>
                                <option value="compliance">Compliance</option>
                                <option value="amendment">Amendments</option>
                            </select>
                            <button
                                onClick={() => setShowUploadModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <Upload size={18} />
                                {t('loanDetail.uploadDocument')}
                            </button>
                        </div>
                    </div>

                    {}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {documents
                            .filter(doc => documentFilter === 'all' || doc.type === documentFilter)
                            .filter(doc => documentSearch === '' || doc.name.toLowerCase().includes(documentSearch.toLowerCase()))
                            .map(doc => {
                            const getTypeColor = () => {
                                switch (doc.type) {
                                    case 'agreement': return 'bg-blue-100 text-blue-700';
                                    case 'financial': return 'bg-emerald-100 text-emerald-700';
                                    case 'compliance': return 'bg-purple-100 text-purple-700';
                                    case 'amendment': return 'bg-amber-100 text-amber-700';
                                    default: return 'bg-slate-100 text-slate-700';
                                }
                            };
                            const getTypeIcon = () => {
                                switch (doc.type) {
                                    case 'agreement': return <FileText size={24} className="text-blue-600" />;
                                    case 'financial': return <BarChart3 size={24} className="text-emerald-600" />;
                                    case 'compliance': return <FileCheck size={24} className="text-purple-600" />;
                                    case 'amendment': return <FilePlus size={24} className="text-amber-600" />;
                                    default: return <File size={24} className="text-slate-600" />;
                                }
                            };
                            return (
                                <div key={doc.id} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="p-2 bg-slate-100 rounded-lg">
                                            {getTypeIcon()}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" title={t('common.download')}>
                                                <Download size={16} className="text-slate-500" />
                                            </button>
                                            <button className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" title={t('common.view')}>
                                                <Eye size={16} className="text-slate-500" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteDocument(doc.id)}
                                                className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                                                title={t('common.delete')}
                                            >
                                                <Trash2 size={16} className="text-slate-500 hover:text-red-500" />
                                            </button>
                                        </div>
                                    </div>
                                    <h4 className="font-medium text-slate-800 text-sm mb-2 line-clamp-2">{doc.name}</h4>
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${getTypeColor()}`}>
                                            {doc.type}
                                        </span>
                                        <span className="text-xs text-slate-500">{doc.size}</span>
                                    </div>
                                    <div className="pt-3 border-t border-slate-100">
                                        <p className="text-xs text-slate-500">
                                            Uploaded by {doc.uploadedBy}
                                        </p>
                                        <p className="text-xs text-slate-400">
                                            {new Date(doc.uploadedAt).toLocaleDateString('en-GB', {
                                                day: 'numeric', month: 'short', year: 'numeric'
                                            })}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {}
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <div className="p-4 sm:p-6 border-b border-slate-200">
                            <h3 className="font-semibold text-slate-800">All Documents</h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Document</th>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Size</th>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Uploaded By</th>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                                        <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                    {documents
                                        .filter(doc => documentFilter === 'all' || doc.type === documentFilter)
                                        .filter(doc => documentSearch === '' || doc.name.toLowerCase().includes(documentSearch.toLowerCase()))
                                        .map(doc => {
                                            const getTypeColor = () => {
                                                switch (doc.type) {
                                                    case 'agreement': return 'bg-blue-100 text-blue-700';
                                                    case 'financial': return 'bg-emerald-100 text-emerald-700';
                                                    case 'compliance': return 'bg-purple-100 text-purple-700';
                                                    case 'amendment': return 'bg-amber-100 text-amber-700';
                                                    default: return 'bg-slate-100 text-slate-700';
                                                }
                                            };
                                            return (
                                                <tr key={doc.id} className="hover:bg-slate-50">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <FileText size={20} className="text-slate-400" />
                                                            <span className="font-medium text-slate-800">{doc.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`text-xs px-2 py-1 rounded-full capitalize ${getTypeColor()}`}>
                                                            {doc.type}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-slate-600">{doc.size}</td>
                                                    <td className="px-6 py-4 text-slate-600">{doc.uploadedBy}</td>
                                                    <td className="px-6 py-4 text-slate-600">
                                                        {new Date(doc.uploadedAt).toLocaleDateString('en-GB')}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <button className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" title={t('common.download')}>
                                                                <Download size={16} className="text-slate-500" />
                                                            </button>
                                                            <button className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" title={t('common.view')}>
                                                                <Eye size={16} className="text-slate-500" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteDocument(doc.id)}
                                                                className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                                                                title={t('common.delete')}
                                                            >
                                                                <Trash2 size={16} className="text-slate-500 hover:text-red-500" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {}
            <ReportPreviewModal
                isOpen={showReportModal}
                onClose={() => setShowReportModal(false)}
                loan={loan}
                alerts={loanAlerts}
                breachHistory={breachHistory}
            />

            {}
            {showUpdateModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
                        <div className="p-6 border-b border-slate-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-xl font-bold text-slate-800">{t('loanDetail.updateCovenantData')}</h2>
                                    <p className="text-sm text-slate-500 mt-1">{t('loanDetail.updateCovenantValues')}</p>
                                </div>
                                <button
                                    onClick={() => { setShowUpdateModal(false); setUpdateMessage({ type: '', text: '' }); }}
                                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5 text-slate-500" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[60vh]">
                            {updateMessage.text && (
                                <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
                                    updateMessage.type === 'success'
                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                        : 'bg-red-50 text-red-700 border border-red-200'
                                }`}>
                                    {updateMessage.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
                                    <span className="text-sm">{updateMessage.text}</span>
                                </div>
                            )}

                            <div className="space-y-4">
                                {loan.covenants.map(covenant => (
                                    <div key={covenant.id} className="p-4 border border-slate-200 rounded-lg">
                                        <div className="flex items-center justify-between mb-3">
                                            <div>
                                                <p className="font-medium text-slate-800">{covenant.name}</p>
                                                <p className="text-xs text-slate-500">{t('loanDetail.threshold')}: {covenant.operator} {covenant.threshold.toLocaleString()}</p>
                                            </div>
                                            <StatusBadge status={covenant.status} />
                                        </div>
                                        <div>
                                            <label className="block text-sm text-slate-600 mb-1">{t('loanDetail.newValue')}</label>
                                            <input
                                                type="number"
                                                value={covenantUpdates[covenant.id] || ''}
                                                onChange={(e) => setCovenantUpdates(prev => ({
                                                    ...prev,
                                                    [covenant.id]: parseFloat(e.target.value) || 0
                                                }))}
                                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                    </div>
                                ))}

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('loanDetail.notes')}</label>
                                    <textarea
                                        value={updateNotes}
                                        onChange={(e) => setUpdateNotes(e.target.value)}
                                        placeholder={t('loanDetail.optionalNotes')}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                        rows={3}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-slate-200 flex gap-3">
                            <button
                                onClick={() => { setShowUpdateModal(false); setUpdateMessage({ type: '', text: '' }); }}
                                className="flex-1 py-2.5 px-4 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleUpdateCovenants}
                                disabled={isUpdating}
                                className="flex-1 py-2.5 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center justify-center gap-2"
                            >
                                {isUpdating ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        {t('loanDetail.saving')}
                                    </>
                                ) : (
                                    t('loanDetail.saveChanges')
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {}
            {showUploadModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                        <div className="p-6 border-b border-slate-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-bold text-slate-800">{t('loanDetail.uploadDocumentTitle')}</h2>
                                <button
                                    onClick={() => { setShowUploadModal(false); setUploadFile(null); setUploadForm({ name: '', type: 'facility_agreement' }); setUpdateMessage({ type: '', text: '' }); }}
                                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5 text-slate-500" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 space-y-4">
                            {updateMessage.text && (
                                <div className={`p-3 rounded-lg flex items-center gap-2 ${
                                    updateMessage.type === 'success'
                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                        : 'bg-red-50 text-red-700 border border-red-200'
                                }`}>
                                    {updateMessage.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
                                    <span className="text-sm">{updateMessage.text}</span>
                                </div>
                            )}

                            {}
                            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                                <input
                                    type="file"
                                    id="file-upload"
                                    className="hidden"
                                    onChange={handleFileSelect}
                                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                                />
                                <label htmlFor="file-upload" className="cursor-pointer">
                                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                                    {uploadFile ? (
                                        <p className="text-sm text-slate-700 font-medium">{uploadFile.name}</p>
                                    ) : (
                                        <>
                                            <p className="text-sm text-slate-600">{t('loanDetail.selectFile')}</p>
                                            <p className="text-xs text-slate-400 mt-1">{t('loanDetail.dragAndDrop')}</p>
                                        </>
                                    )}
                                    <p className="text-xs text-slate-400 mt-2">{t('loanDetail.fileTypes')}</p>
                                </label>
                            </div>

                            {}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">{t('loanDetail.documentName')}</label>
                                <input
                                    type="text"
                                    value={uploadForm.name}
                                    onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder={uploadFile?.name || ''}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">{t('loanDetail.documentType')}</label>
                                <select
                                    value={uploadForm.type}
                                    onChange={(e) => setUploadForm(prev => ({ ...prev, type: e.target.value }))}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="facility_agreement">{t('loanDetail.agreement')}</option>
                                    <option value="financial_statements">{t('loanDetail.financial')}</option>
                                    <option value="compliance_certificate">{t('loanDetail.compliance')}</option>
                                    <option value="amendment">{t('loanDetail.amendments')}</option>
                                    <option value="covenant_report">Covenant Report</option>
                                    <option value="waiver">Waiver</option>
                                    <option value="consent">Consent</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                        </div>

                        <div className="p-6 border-t border-slate-200 flex gap-3">
                            <button
                                onClick={() => { setShowUploadModal(false); setUploadFile(null); setUploadForm({ name: '', type: 'facility_agreement' }); setUpdateMessage({ type: '', text: '' }); }}
                                className="flex-1 py-2.5 px-4 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleUploadDocument}
                                disabled={isUploading || !uploadFile}
                                className="flex-1 py-2.5 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center justify-center gap-2"
                            >
                                {isUploading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        {t('loanDetail.uploading')}
                                    </>
                                ) : (
                                    <>
                                        <Upload size={18} />
                                        {t('loanDetail.uploadDocument')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {}
            {showESGModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
                        <div className="p-6 border-b border-slate-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-emerald-100 rounded-lg">
                                        <Leaf size={24} className="text-emerald-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-slate-800">{t('loanDetail.esg.submitMetricsTitle')}</h2>
                                        <p className="text-sm text-slate-500 mt-1">{t('loanDetail.esg.submitMetricsSubtitle')}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => { setShowESGModal(false); setUpdateMessage({ type: '', text: '' }); }}
                                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5 text-slate-500" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[60vh]">
                            {updateMessage.text && (
                                <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
                                    updateMessage.type === 'success'
                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                        : 'bg-red-50 text-red-700 border border-red-200'
                                }`}>
                                    {updateMessage.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
                                    <span className="text-sm">{updateMessage.text}</span>
                                </div>
                            )}

                            <div className="space-y-6">
                                {}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('loanDetail.esg.reportingPeriod')}</label>
                                    <input
                                        type="month"
                                        value={esgForm.period}
                                        onChange={(e) => setEsgForm(prev => ({ ...prev, period: e.target.value }))}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    />
                                </div>

                                {}
                                <div className="bg-slate-50 rounded-lg p-4">
                                    <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                                        <BarChart3 size={18} className="text-emerald-600" />
                                        {t('loanDetail.esg.overallScores')}
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div>
                                            <label className="block text-xs text-slate-500 mb-1">{t('loanDetail.esg.esgScore')}</label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={esgForm.esg_score}
                                                onChange={(e) => setEsgForm(prev => ({ ...prev, esg_score: e.target.value }))}
                                                placeholder="0-100"
                                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-slate-500 mb-1">E {t('loanDetail.esg.score')}</label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={esgForm.environmental_score}
                                                onChange={(e) => setEsgForm(prev => ({ ...prev, environmental_score: e.target.value }))}
                                                placeholder="0-100"
                                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-slate-500 mb-1">S {t('loanDetail.esg.score')}</label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={esgForm.social_score}
                                                onChange={(e) => setEsgForm(prev => ({ ...prev, social_score: e.target.value }))}
                                                placeholder="0-100"
                                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-slate-500 mb-1">G {t('loanDetail.esg.score')}</label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={esgForm.governance_score}
                                                onChange={(e) => setEsgForm(prev => ({ ...prev, governance_score: e.target.value }))}
                                                placeholder="0-100"
                                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {}
                                <div className="bg-emerald-50 rounded-lg p-4">
                                    <h4 className="font-medium text-emerald-800 mb-3 flex items-center gap-2">
                                        <TrendingDown size={18} className="text-emerald-600" />
                                        {t('loanDetail.esg.environmentalMetrics')}
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs text-emerald-700 mb-1">{t('loanDetail.esg.carbonIntensity')} (tCO2e/M)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={esgForm.carbon_intensity}
                                                onChange={(e) => setEsgForm(prev => ({ ...prev, carbon_intensity: e.target.value }))}
                                                placeholder="e.g., 125.5"
                                                className="w-full px-3 py-2 border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-emerald-700 mb-1">{t('loanDetail.esg.renewableEnergy')} (%)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={esgForm.renewable_energy_percent}
                                                onChange={(e) => setEsgForm(prev => ({ ...prev, renewable_energy_percent: e.target.value }))}
                                                placeholder="0-100"
                                                className="w-full px-3 py-2 border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-emerald-700 mb-1">{t('loanDetail.esg.waterConsumption')} (m³/M)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={esgForm.water_consumption}
                                                onChange={(e) => setEsgForm(prev => ({ ...prev, water_consumption: e.target.value }))}
                                                placeholder="e.g., 5000"
                                                className="w-full px-3 py-2 border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {}
                                <div className="bg-purple-50 rounded-lg p-4">
                                    <h4 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
                                        <Users size={18} className="text-purple-600" />
                                        {t('loanDetail.esg.socialMetrics')}
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs text-purple-700 mb-1">{t('loanDetail.esg.genderDiversity')} (%)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={esgForm.gender_diversity_percent}
                                                onChange={(e) => setEsgForm(prev => ({ ...prev, gender_diversity_percent: e.target.value }))}
                                                placeholder="0-100"
                                                className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-slate-200 flex gap-3">
                            <button
                                onClick={() => { setShowESGModal(false); setUpdateMessage({ type: '', text: '' }); }}
                                className="flex-1 py-2.5 px-4 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleSubmitESG}
                                disabled={isSubmittingESG}
                                className="flex-1 py-2.5 px-4 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-emerald-400 transition-colors flex items-center justify-center gap-2"
                            >
                                {isSubmittingESG ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        {t('loanDetail.esg.submitting')}
                                    </>
                                ) : (
                                    <>
                                        <Leaf size={18} />
                                        {t('loanDetail.esg.submitMetrics')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LoanDetail;